import { delay } from '@intelligence/v3/utils/common';

/**
 * 重试配置接口
 * @author lv.chen
 */
export interface IRetryConfig {
  /** 最大重试次数，默认为 3 */
  maxRetries: number;
  /** 重试延迟时间（毫秒），默认为 1000 */
  retryDelay: number;
}

/**
 * 创建带重试功能的高阶函数
 *
 * 这是一个纯粹的重试高阶函数，不与任何业务逻辑耦合，可以用于任何需要重试的异步操作
 *
 * @author lv.chen
 * @param asyncFn - 需要重试的异步函数
 * @param config - 重试配置
 * @param context - 上下文信息，用于日志记录
 * @returns 带重试功能的函数
 *
 * @example
 * ```typescript
 * const fetchWithRetry = createRetryWrapper(
 *   fetchData,
 *   { maxRetries: 3, retryDelay: 1000 },
 *   'fetchData'
 * );
 * const result = await fetchWithRetry(params);
 * ```
 */
export const createRetryWrapper = <TArgs extends any[], TResult>(
  asyncFn: (...args: TArgs) => Promise<TResult>,
  config: IRetryConfig = { maxRetries: 3, retryDelay: 1000 },
  context = 'operation',
) => {
  const { maxRetries, retryDelay } = config;

  return async (...args: TArgs): Promise<TResult> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await asyncFn(...args);
        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < maxRetries) {
          console.warn(`${context}: Attempt ${attempt + 1} failed, retrying in ${retryDelay}ms...`, lastError.message);
          await delay(retryDelay);
        }
      }
    }

    throw new Error(`${context}: Failed after ${maxRetries + 1} attempts. Last error: ${lastError?.message}`);
  };
};
